// Book Health (C8) — the View → Administration surface that makes the Cause-3
// class visible: which Active desk users have an empty book, which grid manager
// strings resolve to no (or two) IMs, and which stored attributions have gone
// stale against what the shared grid-string rule resolves TODAY.
//
// Read-only; no migration, no Object Type. Computed live from the `users`
// directory, `manager_aliases`, `valuation_grid_portfolios` roster and `clients`
// canonical attribution — so it never drifts from what ingest / the backfill
// would stamp.

import { z } from 'zod';

/** One Active desk user's book shape. */
export const bookHealthDeskUserSchema = z.object({
    userId: z.string(),
    name: z.string().nullable(),
    email: z.string(),
    /** manager_aliases rows bound to this user. */
    aliasCount: z.number(),
    /** The IMs this user acts for, empty for a primary IM. */
    actingForUserIds: z.array(z.string()),
    actingForNames: z.array(z.string()),
    /** Canonical clients across every book the user RESOLVES onto: the books
     * they act for, or their own book when they act for nobody. */
    bookClientCount: z.number(),
    /** Canonical clients attributed to this user's OWN id (hidden when acting-for). */
    ownBookClientCount: z.number(),
});
export type BookHealthDeskUser = z.infer<typeof bookHealthDeskUserSchema>;

/** A grid manager string that resolves to no IM (no_match) or two (ambiguous). */
export const bookHealthUnresolvedManagerSchema = z.object({
    investmentManager: z.string(),
    portfolioCount: z.number(),
    reason: z.enum(['no_match', 'ambiguous']),
    /** For `ambiguous`, the colliding candidate user ids (empty for no_match). */
    candidateUserIds: z.array(z.string()),
});
export type BookHealthUnresolvedManager = z.infer<typeof bookHealthUnresolvedManagerSchema>;

/** A canonical client whose stored IM differs from what the rule resolves now. */
export const bookHealthStaleAttributionSchema = z.object({
    clientId: z.string(),
    clientName: z.string().nullable(),
    storedUserId: z.string().nullable(),
    resolvedUserId: z.string().nullable(),
    reason: z.enum(['no_match', 'ambiguous', 'mismatch']),
});
export type BookHealthStaleAttribution = z.infer<typeof bookHealthStaleAttributionSchema>;

/**
 * A client an admin assigned to an IM by hand (`assign-client-book`). The grid
 * rule no longer decides this row, and an alias re-attribution skips it, so it
 * would otherwise read as a permanent stale attribution. The next ValuationGrid
 * upload re-derives the attribution and clears the assignment, which is why the
 * card names it as pending rather than settled.
 */
export const bookHealthManualAssignmentSchema = z.object({
    clientId: z.string(),
    clientName: z.string().nullable(),
    /** The IM the admin assigned, or null when they detached the client. */
    imUserId: z.string().nullable(),
    imName: z.string().nullable(),
    /** What the grid rule resolves for this client today. */
    resolvedUserId: z.string().nullable(),
    assignedAt: z.string(),
    assignedBy: z.string().nullable(),
    assignedByName: z.string().nullable(),
});
export type BookHealthManualAssignment = z.infer<typeof bookHealthManualAssignmentSchema>;

export const bookHealthResponseSchema = z.object({
    deskUsers: z.array(bookHealthDeskUserSchema),
    unresolvedManagers: z.array(bookHealthUnresolvedManagerSchema),
    staleAttributions: z.array(bookHealthStaleAttributionSchema),
    /** Open manual assignments — excluded from `staleAttributions`, because a
     *  deliberate override is not drift. */
    manualAssignments: z.array(bookHealthManualAssignmentSchema),
});
export type BookHealthResponse = z.infer<typeof bookHealthResponseSchema>;
